import React from 'react'
import {
  Row,
  Col,
  Button,
  Media,
  PopoverBody,
  UncontrolledPopover
} from 'reactstrap'
import ReactHtmlParser from 'react-html-parser'
import classNames from 'classnames'

import { UsersApi } from 'typescript-axios'
import Link from 'next/link'
import ShareModal, { ShareProfileCta } from './ShareModal'
import { getAxiosParams } from '../utils/client'
import useTranslation from '../hooks/UseTranslation'
import { UserContext } from '../contexts/UserContext'

const USERS_SERVICE = new UsersApi(getAxiosParams())

export const GuruReferralCta = ({
  user,
  listing,
  retreat,
  className,
  showContent,
  children
}) => {
  const { t } = useTranslation()
  const [guruReferrable, setGuruReferrable] = React.useState(
    user ?? (listing?.owner ?? retreat?.owner)
  )
  const [showShareModal, setShowShareModal] = React.useState(false)
  const { loggedUser } = React.useContext(UserContext)

  React.useEffect(() => {
    if (!!loggedUser && !guruReferrable) {
      USERS_SERVICE.readReferrableGuruApiV1UsersReferrableGuruGet()
        .then((res) => setGuruReferrable(res.data))
        .catch(console.error)
    }
  }, [])

  if (
    !guruReferrable ||
    !guruReferrable.has_referral_enabled ||
    !loggedUser ||
    loggedUser?.id == guruReferrable?.id
  )
    return (
      <div>
        <ShareProfileCta user={user} listing={listing} retreat={retreat}>
          {children}
        </ShareProfileCta>
        {!!showContent &&
          !!guruReferrable &&
          !!guruReferrable.has_referral_enabled &&
          loggedUser?.id == guruReferrable?.id && (
            <div className='mt-2 font-weight-bold'>
              {t('guru_referral_enabled_content_guru')}
              <Link href='/fidelisation#referral'>
                <a>
                  <i
                    id='guru_referral_question'
                    className='fa fa-question-circle fa-fw ml-1 text-muted pointer'
                  />
                  <UncontrolledPopover
                    trigger='hover focus'
                    placement='bottom'
                    target='guru_referral_question'
                  >
                    <PopoverBody>
                      {t('guru_referral_policy_popover')}
                    </PopoverBody>
                  </UncontrolledPopover>
                </a>
              </Link>
            </div>
          )}
      </div>
    )

  const { referrals_free_classes, n_ref_threshold } = guruReferrable

  let shareUrl
  if (listing) {
    shareUrl = `${process.env.NEXT_PUBLIC_HOST}/classes/${listing.slug}?referral=${loggedUser.guru_code}`
  } else if (retreat) {
    shareUrl = `${process.env.NEXT_PUBLIC_HOST}/retreats/${retreat.slug}?referral=${loggedUser.guru_code}`
  } else {
    shareUrl = `${process.env.NEXT_PUBLIC_HOST}/@${guruReferrable.slug}?referral=${loggedUser.guru_code}`
  }

  const onClick = (e) => {
    e.preventDefault()
    setShowShareModal(true)
  }

  const getHeader = () =>
    ReactHtmlParser(
      t(
        'guru_referral_policy_to_user_header',
        guruReferrable.name_display,
        referrals_free_classes,
        n_ref_threshold
      )
    )

  // key necessary because FA icon does not rerender, do not remove
  const label = (
    <>
      <i key={Math.random()} className='fa fa-gift fa-fw mr-2' />
      {t('guru_referral_user_cta')}
    </>
  )

  return (
    <div>
      <ShareModal
        header={getHeader()}
        shareUrl={shareUrl}
        isOpen={showShareModal}
        toggle={() => setShowShareModal(!showShareModal)}
      >
        <Media className='text-sm text-muted'>
          <Media body className='mt-n1 mb-2'>
            {ReactHtmlParser(t('guru_referral_policy_to_user_modal_content'))}
          </Media>
        </Media>
      </ShareModal>
      {children ? (
        children(onClick, label)
      ) : (
        <Button
          color='link'
          className={classNames('p-0 text-sm mb-2 text-secondary')}
          onClick={onClick}
        >
          {label}
        </Button>
      )}
      {!!showContent && (
        <div className='mt-2 font-weight-bold'>{getHeader()}</div>
      )}
    </div>
  )
}

const getReferralContent = (
  guruFirstname,
  n_free_classes,
  n_refs,
  guru_code,
  t
) => (
  <Media className='text-sm text-muted'>
    <Media body className='mt-n1'>
      {ReactHtmlParser(
        t(
          'guru_referral_policy_to_user_header',
          guruFirstname,
          n_free_classes,
          n_refs,
          guru_code
        )
      )}
      {ReactHtmlParser(
        t('guru_referral_policy_to_user_content', guru_code, guruFirstname)
      )}
    </Media>
  </Media>
)

const GuruReferralForUser = () => {
  const { t } = useTranslation()
  const { loggedUser } = React.useContext(UserContext)
  const [hasCopied, setHasCopied] = React.useState(false)

  const [guruReferrable, setGuruReferrable] = React.useState()

  React.useEffect(() => {
    USERS_SERVICE.readReferrableGuruApiV1UsersReferrableGuruGet()
      .then((res) => setGuruReferrable(res.data))
      .catch(console.error)
  }, [])

  if (!guruReferrable || !guruReferrable.has_referral_enabled) return ''

  const { referrals_free_classes, n_ref_threshold } = guruReferrable
  return (
    <>
      <Row className='mb-3'>
        <Col sm='8'>
          <h5 className='text-first-letter'>
            {t('guru_referral_title_to_user', guruReferrable.name_display)}
          </h5>
        </Col>
        <Col sm='4' className='text-right'>
          <GuruReferralCta guru={guruReferrable} />
        </Col>
      </Row>
      {getReferralContent(
        guruReferrable.name_display,
        referrals_free_classes,
        n_ref_threshold,
        loggedUser.guru_code,
        t
      )}
    </>
  )
}

export default GuruReferralForUser
